import React, { useCallback, useMemo } from 'react'
import {
  Button,
  Drawer,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'

import { EPipelineDisplayType, IFilterMapped } from './PipelineFilter'
import DateRangePicker from '../../../../../components/DateRangePicker'

interface PipelineFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filter: IFilterMapped;
  onFilterChange: (filter: IFilterMapped) => void;
  onSearch: (filter: IFilterMapped) => void;
}

const PipelineFilterDrawer = ({
                                isOpen,
                                onClose,
                                onFilterChange,
                                filter,
                                onSearch,
                              }: PipelineFilterDrawerProps) => {
  const { page, perPage, search, startDate, endDate, displayType } = filter
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const menuItemsTypeDisplay = useMemo(() => [
    { label: 'แสดงทั้งหมด', value: "all" },
    { label: 'เฉพาะรายการลบ', value: EPipelineDisplayType.DELETE },
    { label: 'เฉพาะที่ใช้งาน', value: EPipelineDisplayType.USED },
  ], [])

  const handleFilterChange = useCallback((newFilter: IFilterMapped) => {
    onFilterChange({ ...filter, ...newFilter })
  }, [filter, onFilterChange])

  const handleReset = useCallback(() => {
    onFilterChange({
      page: page,
      perPage: perPage,
      search: undefined,
      startDate: undefined,
      endDate: undefined,
      displayType: undefined,
    })
    onSearch({
      page: page,
      perPage: perPage,
      search: undefined,
      startDate: undefined,
      endDate: undefined,
      displayType: undefined,
    })
  }, [page, perPage, onFilterChange, onSearch])

  return (
    <Drawer
      anchor={'right'}
      keepMounted
      open={isOpen}
      onClose={onClose}
      PaperProps={{ style: { width: isMobile ? '80%' : '40%', maxWidth: isMobile ? '80%' : '40%' } }}
    >
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item xs={12} lg={12}>
          <TextField
            InputProps={{
              startAdornment: (<InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>),
            }}
            label={'ค้นหา Pipeline'}
            fullWidth
            id="search"
            name="search"
            autoFocus
            variant="filled"
            value={search || ''}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={(startDate, endDate) => {
              handleFilterChange({ startDate, endDate })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="dateType-label">รายการแสดงผล</InputLabel>
            <Select
              id="dateType"
              name="dateType"
              labelId="dateType-label"
              value={displayType}
              onChange={(e) => handleFilterChange({ displayType: e.target.value as EPipelineDisplayType })}
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกรายการแสดงผล</em>
              </MenuItem>
              {menuItemsTypeDisplay.map((item, index: number) => (
                <MenuItem value={item.value} key={index}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={6} />
        <Grid item xs={9} lg={9}>
          <Button
            color="primary"
            size="large"
            fullWidth
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => onSearch(filter)}
            sx={{ height: '100%' }}
          >
            ค้นหา
          </Button>
        </Grid>
        <Grid item xs={3} lg={3}>
          <Button
            color="inherit"
            size="large"
            fullWidth
            variant="contained"
            startIcon={!isMobile && <ClearIcon />}
            onClick={() => handleReset()}
            sx={{ height: '100%' }}
          >
            {isMobile ? <ClearIcon /> : 'รีเซ็ต'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  )
}

export default PipelineFilterDrawer